/**
 * Sprint Overview Card Component
 * Displays sprint details with tabs for Overview, Updates, Files, and Activity
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Chip,
  Divider,
  LinearProgress,
  Paper,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import UpdateIcon from '@mui/icons-material/Update';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import TimelineIcon from '@mui/icons-material/Timeline';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TaskIcon from '@mui/icons-material/Task';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { fetchSprintOverview } from '../../store/sprintSlice';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './SprintOverviewCard.scss';

dayjs.extend(relativeTime);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sprint-tabpanel-${index}`}
      aria-labelledby={`sprint-tab-${index}`}
      className="tab-panel"
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

interface SprintOverviewCardProps {
  sprintId: string;
}

const SprintOverviewCard: React.FC<SprintOverviewCardProps> = ({ sprintId }) => {
  const dispatch = useAppDispatch();
  const { sprintOverview, currentSprint } = useAppSelector((state) => state.sprints);
  const { tasks } = useAppSelector((state) => state.tasks);
  const sprintTasks = (tasks || []).filter((t) => String(t.sprintId) === String(sprintId));

  const [currentTab, setCurrentTab] = useState(0);
  const [updateText, setUpdateText] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (sprintId) {
      dispatch(fetchSprintOverview(sprintId));
    }
  }, [sprintId, dispatch]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handlePostUpdate = () => {
    if (updateText.trim()) {
      // TODO: Dispatch action to add sprint update
      console.log('Posting update:', updateText);
      setUpdateText('');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setUploading(true);
      // TODO: Implement file upload
      console.log('Uploading files:', files);
      setTimeout(() => setUploading(false), 2000);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'sprint_started':
        return <CheckCircleIcon color="success" />;
      case 'task_completed':
        return <CheckCircleIcon color="primary" />;
      case 'update_posted':
        return <UpdateIcon color="info" />;
      case 'file_uploaded':
        return <AttachFileIcon color="action" />;
      default:
        return <TimelineIcon />;
    }
  };

  return (
    <Card className="sprint-overview-card">
      <CardContent>
        {/* Header */}
        <Box className="overview-header">
          <DescriptionIcon color="primary" />
          <Typography variant="h6">Sprint Overview</Typography>
        </Box>

        {/* Tabs */}
        <Box className="tabs-container">
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="fullWidth"
            className="overview-tabs"
          >
            <Tab label="Overview" icon={<DescriptionIcon />} iconPosition="start" />
            <Tab label="Updates" icon={<UpdateIcon />} iconPosition="start" />
            <Tab label="Files" icon={<AttachFileIcon />} iconPosition="start" />
            <Tab label="Activity" icon={<TimelineIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={currentTab} index={0}>
          <Box className="overview-content">
            {/* Sprint Details */}
            <Box className="detail-section">
              <Typography variant="subtitle2" className="section-title">
                Sprint Information
              </Typography>
              <Box className="info-grid">
                <Box className="info-item">
                  <Typography variant="caption" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {currentSprint?.startDate
                      ? dayjs(currentSprint.startDate).format('MMM D, YYYY')
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box className="info-item">
                  <Typography variant="caption" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {currentSprint?.endDate
                      ? dayjs(currentSprint.endDate).format('MMM D, YYYY')
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box className="info-item">
                  <Typography variant="caption" color="text.secondary">
                    Total Points
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {currentSprint?.totalStoryPoints || 0}
                  </Typography>
                </Box>
                <Box className="info-item">
                  <Typography variant="caption" color="text.secondary">
                    Completed Points
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {currentSprint?.completedStoryPoints || 0}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Sprint Goals */}
            {sprintOverview?.goals && sprintOverview.goals.length > 0 && (
              <Box className="detail-section">
                <Typography variant="subtitle2" className="section-title">
                  Sprint Goals
                </Typography>
                <List className="goals-list">
                  {sprintOverview.goals.map((goal: any) => (
                    <ListItem key={goal.id} className="goal-item">
                      <ListItemAvatar>
                        <Avatar
                          className={`goal-avatar ${
                            goal.isCompleted ? 'completed' : 'pending'
                          }`}
                        >
                          {goal.isCompleted ? (
                            <CheckCircleIcon />
                          ) : (
                            goal.currentValue || 0
                          )}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={goal.description}
                        secondary={
                          goal.targetValue
                            ? `${goal.currentValue || 0} / ${goal.targetValue} points`
                            : undefined
                        }
                      />
                      {goal.targetValue && (
                        <Box className="goal-progress">
                          <LinearProgress
                            variant="determinate"
                            value={
                              ((goal.currentValue || 0) / goal.targetValue) * 100
                            }
                            className={
                              goal.isCompleted ? 'completed' : 'in-progress'
                            }
                          />
                        </Box>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Team Members */}
            {sprintOverview?.teamMembers && sprintOverview.teamMembers.length > 0 && (
              <Box className="detail-section">
                <Typography variant="subtitle2" className="section-title">
                  Team Members
                </Typography>
                <Box className="team-members">
                  {sprintOverview.teamMembers.map((member: any) => (
                    <Chip
                      key={member.id}
                      avatar={<Avatar>{member.name.charAt(0)}</Avatar>}
                      label={member.name}
                      variant="outlined"
                      className="member-chip"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Sprint Tasks */}
            <Box className="detail-section" sx={{ mt: 3 }}>
              <Typography variant="subtitle2" className="section-title">
                Sprint Tasks ({sprintTasks.length})
              </Typography>
              {sprintTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No tasks assigned to this sprint yet.
                </Typography>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    maxHeight: 360,
                    overflowY: 'auto',
                    borderColor: '#e2e8f0',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f5f9',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#cbd5e1',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: '#94a3b8',
                    },
                  }}
                >
                  <List disablePadding>
                    {sprintTasks.map((task, index) => (
                      <React.Fragment key={task.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1.5,
                            px: 2,
                            bgcolor: '#ffffff',
                            '&:hover': { bgcolor: '#f8fafc' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <TaskIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                            <Typography variant="body2" fontWeight={600} color="#0f172a">
                              {task.title}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {task.type && (
                              <Chip
                                label={task.type.toUpperCase()}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                              />
                            )}
                            {task.priority && (
                              <Chip
                                label={task.priority.toUpperCase()}
                                size="small"
                                color={
                                  task.priority === 'urgent' || task.priority === 'high'
                                    ? 'error'
                                    : 'default'
                                }
                                sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                              />
                            )}
                            <Chip
                              label={(task.status || 'todo').replace('_', ' ').toUpperCase()}
                              size="small"
                              color={task.status === 'done' ? 'success' : 'primary'}
                              sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                            />
                            {task.storyPoints !== undefined && task.storyPoints > 0 && (
                              <Chip
                                label={`${task.storyPoints} SP`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                              />
                            )}
                          </Box>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Box className="updates-content">
            {/* Post Update */}
            <Box className="post-update">
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Share an update about the sprint..."
                value={updateText}
                onChange={(e) => setUpdateText(e.target.value)}
                variant="outlined"
                className="update-input"
              />
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={handlePostUpdate}
                disabled={!updateText.trim()}
                className="post-button"
              >
                Post Update
              </Button>
            </Box>

            <Divider className="updates-divider" />

            {/* Updates List */}
            <List className="updates-list">
              {sprintOverview?.updates && sprintOverview.updates.length > 0 ? (
                sprintOverview.updates.map((update: any) => (
                  <ListItem key={update.id} className="update-item" alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar>{update.authorName.charAt(0)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box className="update-header">
                          <Typography variant="subtitle2" fontWeight={600}>
                            {update.authorName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(update.createdAt).fromNow()}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" className="update-text">
                          {update.content}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <Box className="empty-state">
                  <UpdateIcon fontSize="large" color="disabled" />
                  <Typography variant="body2" color="text.secondary">
                    No updates yet. Be the first to share!
                  </Typography>
                </Box>
              )}
            </List>
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Box className="files-content">
            {/* Upload Section */}
            <Box className="upload-section">
              <input
                accept="*/*"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                multiple
                onChange={handleFileUpload}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<AttachFileIcon />}
                  disabled={uploading}
                  className="upload-button"
                >
                  {uploading ? 'Uploading...' : 'Upload Files'}
                </Button>
              </label>
            </Box>

            {/* Files List */}
            <List className="files-list">
              {sprintOverview?.files && sprintOverview.files.length > 0 ? (
                sprintOverview.files.map((file: any) => (
                  <ListItem
                    key={file.id}
                    className="file-item"
                    secondaryAction={
                      <Box className="file-actions">
                        <IconButton edge="end" aria-label="download">
                          <DownloadIcon />
                        </IconButton>
                        <IconButton edge="end" aria-label="delete">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar className="file-avatar">
                        <AttachFileIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={file.name}
                      secondary={`${formatFileSize(file.size)} • Uploaded ${dayjs(
                        file.uploadedAt
                      ).fromNow()}`}
                    />
                  </ListItem>
                ))
              ) : (
                <Box className="empty-state">
                  <AttachFileIcon fontSize="large" color="disabled" />
                  <Typography variant="body2" color="text.secondary">
                    No files uploaded yet
                  </Typography>
                </Box>
              )}
            </List>
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Box className="activity-content">
            <List className="activity-list">
              {sprintOverview?.activities && sprintOverview.activities.length > 0 ? (
                sprintOverview.activities.map((activity: any) => (
                  <ListItem key={activity.id} className="activity-item">
                    <ListItemAvatar>
                      <Avatar className="activity-avatar">
                        {getActivityIcon(activity.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={activity.description}
                      secondary={dayjs(activity.timestamp).fromNow()}
                    />
                  </ListItem>
                ))
              ) : (
                <Box className="empty-state">
                  <TimelineIcon fontSize="large" color="disabled" />
                  <Typography variant="body2" color="text.secondary">
                    No activity recorded yet
                  </Typography>
                </Box>
              )}
            </List>
          </Box>
        </TabPanel>
      </CardContent>
    </Card>
  );
};

export default SprintOverviewCard;
